/**
 * The AI test call, as a dialog: read what the AI will say and know, then ring.
 *
 * Opened by `AiTestCallHost`. It asks the server for the preview first (the
 * exact opening, the brain's arguments and script, and why a real call would or
 * would not happen), and only the button at the bottom places the call, to a
 * test number, never the company. Nothing is rung by opening the dialog.
 *
 * Opened from a campaign's queue (ring R8) the target names the campaign: the
 * call then uses its words and its call flow map, and the dialog says which
 * version of the map the call will follow, or that the map is broken and the
 * call would be refused.
 *
 * Opened for an alert campaign (ring R9) it shows the defect the call would be
 * about (`AlertEvidence`). A company with no defect worth a call gets a note
 * and no call button: the route would refuse it with `no_alert`. A refusal from
 * the pre-dial recheck reads the preview again (`staleAlertRefusal.ts`) and
 * keeps the refusal on screen while it does, so the next press rings about
 * the defect shown, or is not offered.
 *
 * Above every CRM modal (`z-[95]`): the brain card opens it from inside the
 * brain dialog on the leads list, and the kit's own layer would sit under that.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Loader2, PhoneOutgoing, RefreshCw } from "lucide-react";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import {
  invalidateAiOutboundStatus,
  placeAiOutboundTestCall,
  realCallSkip,
  testCallError,
} from "../../../lib/aiOutbound";
import { openingProblemKey } from "../../../lib/aiOutboundOpening";
import {
  previewAiOutboundTestCall,
  testCallBody,
  testCallNoAlert,
  type AiOutboundTestCallPreview,
  type AiTestCallTarget,
} from "../../../lib/aiTestCallPreview";
import { formatPhone } from "../../../lib/phone";
import { AiKnows } from "./AiKnows";
import { AlertEvidence } from "./AlertEvidence";
import { refusalRereadsPreview } from "./staleAlertRefusal";

type Load = { state: "loading" } | { state: "error"; key: string } | { state: "ready"; preview: AiOutboundTestCallPreview };
type Placed = { state: "idle" } | { state: "placing" } | { state: "placed"; message: string } | { state: "failed"; message: string };

export function AiTestCallDialog({ target, onClose }: { target: AiTestCallTarget; onClose: () => void }) {
  const t = useT();
  const [load, setLoad] = useState<Load>({ state: "loading" });
  const [placed, setPlaced] = useState<Placed>({ state: "idle" });
  const alive = useRef(true);

  const fetchPreview = useCallback(() => {
    setLoad({ state: "loading" });
    previewAiOutboundTestCall(target)
      .then((preview) => {
        if (alive.current) setLoad({ state: "ready", preview });
      })
      .catch((err: unknown) => {
        if (alive.current) setLoad({ state: "error", key: testCallError(err).key });
      });
  }, [target]);

  useEffect(() => {
    alive.current = true;
    fetchPreview();
    return () => {
      alive.current = false;
    };
  }, [fetchPreview]);

  const preview = load.state === "ready" ? load.preview : null;
  const blocker = preview?.blockers[0] ?? null;
  const flowBroken = preview?.flow?.status === "invalid";
  const noAlert = testCallNoAlert(preview);
  const canPlace =
    preview !== null &&
    blocker === null &&
    preview.openingProblems.length === 0 &&
    !flowBroken &&
    !noAlert &&
    placed.state !== "placing" &&
    placed.state !== "placed";

  async function place() {
    setPlaced({ state: "placing" });
    try {
      const res = await placeAiOutboundTestCall(testCallBody(target));
      const message = t("aiOutbound.testCall.placed", { number: formatPhone(res.toNumber) });
      if (alive.current) setPlaced({ state: "placed", message });
    } catch (err) {
      const error = testCallError(err);
      const message = t(`aiOutbound.testCall.error.${error.key}`, {
        cap: error.cap ?? "",
        number: error.number ? formatPhone(error.number) : "",
      });
      if (error.refreshStatus) invalidateAiOutboundStatus();
      if (!alive.current) return;
      setPlaced({ state: "failed", message });
      // The recheck may have resolved this defect; the next press picks again.
      if (refusalRereadsPreview(error.key)) fetchPreview();
    }
  }

  const title = preview?.company.name || target.name || preview?.company.domain || target.domain || target.regCode || "";
  const skip = preview ? realCallSkip({ notes: [], realCall: { ...preview.realCall, legalState: "register_company" } }) : null;

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent className="z-[95] max-h-[92dvh] max-w-2xl overflow-y-auto" overlayClassName="z-[95]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneOutgoing className="size-4 text-primary" />
            {t("aiOutbound.testCall.dialog.title")}
          </DialogTitle>
          <DialogDescription>
            {title}
            {preview?.company.domain && preview.company.domain !== title ? ` - ${preview.company.domain}` : ""}
          </DialogDescription>
        </DialogHeader>

        {load.state === "loading" ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t("aiOutbound.testCall.dialog.loading")}
          </div>
        ) : null}

        {load.state === "error" ? (
          <div className="space-y-2">
            <p className="crm-alert">
              <AlertTriangle className="size-4 shrink-0" />
              {t(`aiOutbound.testCall.error.${load.key}`, { cap: "", number: "" })}
            </p>
            <Button variant="outline" size="sm" onClick={fetchPreview}>
              <RefreshCw className="size-3.5" />
              {t("aiOutbound.testCall.dialog.retry")}
            </Button>
          </div>
        ) : null}

        {preview ? (
          <div className="space-y-4">
            {preview.alert ? <AlertEvidence alert={preview.alert} /> : null}

            {noAlert ? null : (
              <section className="space-y-1.5">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t("aiOutbound.testCall.dialog.opening")}
                </h4>
                <blockquote className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm leading-relaxed">
                  {preview.openingText}
                </blockquote>
                {preview.openingProblems.length > 0 ? (
                  <p className="crm-alert">
                    <AlertTriangle className="size-4 shrink-0" />
                    {t("aiOutbound.testCall.dialog.openingProblems", {
                      problems: preview.openingProblems.map((p) => t(`aiOutbound.opening.problem.${openingProblemKey(p)}`)).join(", "),
                    })}
                  </p>
                ) : null}
              </section>
            )}

            {preview.flow ? (
              preview.flow.status === "invalid" ? (
                <p className="crm-alert">
                  <AlertTriangle className="size-4 shrink-0" />
                  {t("aiOutbound.testCall.dialog.flow.invalid", { n: preview.flow.problems })}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {preview.flow.status === "ok"
                    ? t("aiOutbound.testCall.dialog.flow.ok", { version: preview.flow.version, n: preview.flow.steps })
                    : t("aiOutbound.testCall.dialog.flow.none")}
                </p>
              )
            ) : null}

            <section className="space-y-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t("aiOutbound.testCall.dialog.knows")}
              </h4>
              <AiKnows knows={preview.knows} domain={preview.company.domain} onNavigate={onClose} />
            </section>

            {skip ? (
              <p className="text-xs text-muted-foreground">
                {t("aiOutbound.testCall.realWouldSkip", {
                  reason: t(`aiOutbound.testCall.skip.${skip.key}`, { code: skip.code }),
                })}
              </p>
            ) : null}

            {blocker ? (
              <p className="text-sm text-muted-foreground">
                {t("aiOutbound.testCall.notReady", { reason: t(`aiOutbound.testCall.reason.${blocker}`, { code: blocker }) })}{" "}
                <Link to="/settings/phone/ai-outbound" className="text-primary hover:underline" onClick={onClose}>
                  {t("aiOutbound.testCall.settingsLink")}
                </Link>
              </p>
            ) : null}
          </div>
        ) : null}

        {/* Outside the preview: a refusal stays visible while the preview is read again. */}
        {placed.state === "placed" ? (
          <p className="flex items-start gap-2 rounded-lg bg-green-500/10 px-3 py-2 text-sm text-green-700 dark:text-green-400" role="status">
            <CheckCircle2 className="mt-0.5 size-4 shrink-0" />
            <span>
              {placed.message} {t("aiOutbound.testCall.dialog.resultLater")}{" "}
              <Link to="/ai-calls" className="underline" onClick={onClose}>
                {t("aiOutbound.testCall.dialog.resultsLink")}
              </Link>
            </span>
          </p>
        ) : null}
        {placed.state === "failed" ? (
          <p className="crm-alert" role="alert">
            <AlertTriangle className="size-4 shrink-0" />
            {placed.message}
          </p>
        ) : null}

        <DialogFooter className="flex-wrap">
          <Button variant="ghost" onClick={onClose}>
            {t("aiOutbound.testCall.dialog.close")}
          </Button>
          <Button onClick={() => void place()} disabled={!canPlace} loading={placed.state === "placing"}>
            <PhoneOutgoing className="size-4" />
            {t("aiOutbound.testCall.dialog.call")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
